// components/journey/new-journey/node-config-modal.tsx
import React, { useState, useEffect } from "react";
import { X, Save, Settings } from "lucide-react";
import { Node } from "../../../../pages/journey/types";

interface NodeConfigModalProps {
  node: Node | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (nodeId: string, data: any) => void;
}

export const NodeConfigModal: React.FC<NodeConfigModalProps> = ({
  node,
  isOpen,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState<any>({});

  useEffect(() => {
    if (node) {
      setFormData(node.data || {});
    }
  }, [node]);

  if (!isOpen || !node) return null;

  const handleInputChange = (key: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    onSave(node.id, formData);
    onClose();
  };

  const renderWhatsAppConfig = () => (
    <div className="space-y-6">
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Parameters</h4>
        
        {/* Settings */}
        <div className="space-y-4">
          <div>
            <h5 className="text-sm font-medium text-gray-700 mb-3">Settings</h5>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Credential to connect with
                </label>
                <select
                  value={formData.credential || ""}
                  onChange={(e) => handleInputChange("credential", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Credential</option>
                  <option value="whatsapp-business">WhatsApp Business API</option>
                  <option value="twilio">Twilio</option>
                </select>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <h5 className="text-sm font-medium text-gray-700 mb-3">Resource</h5>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Message</label>
                <select
                  value={formData.resource || "message"}
                  onChange={(e) => handleInputChange("resource", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="message">Message</option>
                  <option value="template">Template</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Operation</label>
                <select
                  value={formData.operation || "send"}
                  onChange={(e) => handleInputChange("operation", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="send">Send</option>
                  <option value="send-wait">Send and Wait for Response</option>
                </select>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <h5 className="text-sm font-medium text-gray-700 mb-3">Send Message To</h5>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-600 mb-1">User</label>
                <select
                  value={formData.sendTo || "user"}
                  onChange={(e) => handleInputChange("sendTo", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="user">User</option>
                  <option value="group">Group</option>
                  <option value="list">From list</option>
                </select>
              </div>
              {formData.sendTo === "list" && (
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Select a user...</label>
                  <select
                    value={formData.userList || ""}
                    onChange={(e) => handleInputChange("userList", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select a user...</option>
                    <option value="user1">User 1</option>
                    <option value="user2">User 2</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <h5 className="text-sm font-medium text-gray-700 mb-3">Message</h5>
            <textarea
              value={formData.message || ""}
              onChange={(e) => handleInputChange("message", e.target.value)}
              rows={6}
              placeholder="Type your message here..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderTriggerConfig = () => (
    <div className="space-y-6">
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Trigger Settings</h4>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Trigger Type</label>
            <select
              value={formData.triggerType || "schedule"}
              onChange={(e) => handleInputChange("triggerType", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="schedule">Schedule</option>
              <option value="webhook">Webhook</option>
              <option value="manual">Manual</option>
              <option value="event">Event</option>
            </select>
          </div>

          {formData.triggerType === "schedule" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Schedule Type</label>
              <select
                value={formData.scheduleType || "cron"}
                onChange={(e) => handleInputChange("scheduleType", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-emerald-500 focus:border-emerald-500"
              >
                <option value="cron">Cron Expression</option>
                <option value="interval">Interval</option>
                <option value="specific">Specific Time</option>
              </select>
              
              {formData.scheduleType === "cron" && (
                <div className="mt-2">
                  <label className="block text-sm text-gray-600 mb-1">Cron Expression</label>
                  <input
                    type="text"
                    value={formData.cron || "0 0 * * *"}
                    onChange={(e) => handleInputChange("cron", e.target.value)}
                    placeholder="0 0 * * *"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderConfigByType = () => {
    switch (node.type) {
      case "trigger":
        return renderTriggerConfig();
      case "whatsapp":
        return renderWhatsAppConfig();
      case "sms":
      case "email":
      case "ai_call":
      case "rcs":
        return renderWhatsAppConfig();
      default:
        return (
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Label</label>
                <input
                  type="text"
                  value={formData.label || node.label}
                  onChange={(e) => handleInputChange("label", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
              <Settings className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Configure {node.label} Node
              </h2>
              <p className="text-sm text-gray-500">
                Set up the node parameters and settings
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {renderConfigByType()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
          >
            <Save className="w-4 h-4" />
            <span>Save Configuration</span>
          </button>
        </div>
      </div>
    </div>
  );
};